import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, PropsWithChildren, useRef } from 'react';
import { useGetMyActionsLazyQuery } from 'generated/graphql';
import useIsAuthenticated from 'react-auth-kit/hooks/useIsAuthenticated';
import useAuthHeader from 'react-auth-kit/hooks/useAuthHeader';

/**
 * Context for managing user's granted actions (permissions).
 * This enables action-based authorization checks in the UI.
 */

interface ActionsContextValue {
  /** Set of actions granted to the current user */
  actions: Set<string>;
  /** Whether actions are still loading */
  isLoading: boolean;
  /** Whether actions have been loaded (even if empty) */
  isReady: boolean;
  /** Check if the user has a specific action */
  hasAction: (action: string) => boolean;
  /** Check if the user has any of the specified actions */
  hasAnyAction: (actions: readonly string[]) => boolean;
  /** Check if the user has all of the specified actions */
  hasAllActions: (actions: readonly string[]) => boolean;
  /** Force refresh actions from the server */
  refreshActions: () => Promise<void>;
}

const ActionsContext = createContext<ActionsContextValue | null>(null);

/**
 * Provider component that fetches and manages the current user's granted actions.
 * Must be placed inside ApolloProvider and AuthProvider.
 */

// Use window-level state to persist actions across component remounts AND code bundles
// This ensures actions survive even when multiple code chunks are loaded
declare global {
  interface Window {
    __CSC_ACTIONS__?: Set<string>;
    __CSC_ACTIONS_FETCHED__?: boolean;
  }
}

function getGlobalActions(): Set<string> {
  if (typeof window === 'undefined') return new Set();
  return window.__CSC_ACTIONS__ || new Set();
}

function setGlobalActions(actions: Set<string>) {
  if (typeof window === 'undefined') return;
  window.__CSC_ACTIONS__ = actions;
  window.__CSC_ACTIONS_FETCHED__ = true;
}

function clearGlobalActions() {
  if (typeof window === 'undefined') return;
  window.__CSC_ACTIONS__ = new Set();
  window.__CSC_ACTIONS_FETCHED__ = false;
}

function hasGlobalActionsFetched(): boolean {
  if (typeof window === 'undefined') return false;
  return window.__CSC_ACTIONS_FETCHED__ === true;
}

export function ActionsProvider({ children }: PropsWithChildren) {
  const isAuthenticated = useIsAuthenticated();
  const authHeader = useAuthHeader();

  // Initialize from window-level state to survive remounts AND code bundle boundaries
  const [actions, setActionsState] = useState<Set<string>>(() => getGlobalActions());
  const [isReady, setIsReady] = useState(() => hasGlobalActionsFetched());
  const isFetchingRef = useRef(false);

  // Force re-check counter to trigger effect re-runs when token appears
  const [checkCounter, setCheckCounter] = useState(0);

  const [fetchActions, { loading }] = useGetMyActionsLazyQuery({
    fetchPolicy: 'network-only',
  });

  // Wrapper to update both component state and window-level state
  const setActions = useCallback((newActions: Set<string>) => {
    setActionsState(newActions);
    setGlobalActions(newActions);
  }, []);

  // Check for auth token directly from localStorage (most reliable source)
  const hasToken = typeof window !== 'undefined' && !!localStorage.getItem('_auth');
  const effectivelyAuthenticated = isAuthenticated || hasToken;

  // Force re-check after mount to catch tokens that exist but weren't detected initially
  useEffect(() => {
    const timer = setTimeout(() => {
      const token = localStorage.getItem('_auth');
      if (token && actions.size === 0 && !hasGlobalActionsFetched()) {
        console.log('[ActionsContext] Delayed token detection - forcing re-check');
        setCheckCounter(c => c + 1);
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [actions.size]);

  // Fetch actions when authenticated
  useEffect(() => {
    // Check localStorage directly inside effect to get current value
    const currentToken = typeof window !== 'undefined' ? localStorage.getItem('_auth') : null;
    const hasTokenNow = !!currentToken;
    const isAuthNow = isAuthenticated || hasTokenNow;
    const globalActions = getGlobalActions();
    const globalFetched = hasGlobalActionsFetched();

    console.log('[ActionsContext] Effect - isAuthenticated:', isAuthenticated, 'hasTokenNow:', hasTokenNow, 'tokenLength:', currentToken?.length ?? 0, 'actions:', actions.size, 'globalActions:', globalActions.size, 'globalFetched:', globalFetched, 'isReady:', isReady, 'isFetching:', isFetchingRef.current);

    // If we have window-level actions from a previous mount/bundle, use them
    if (globalFetched && globalActions.size > 0 && actions.size === 0) {
      console.log('[ActionsContext] Restoring actions from window state:', globalActions.size);
      setActionsState(globalActions);
      setIsReady(true);
      return;
    }

    // If authenticated and we don't have actions yet (and haven't fetched), fetch them
    if (isAuthNow && actions.size === 0 && !globalFetched && !isFetchingRef.current) {
      isFetchingRef.current = true;
      setIsReady(false);
      console.log('[ActionsContext] Fetching user actions...');

      fetchActions().then(result => {
        console.log('[ActionsContext] Fetch result:', result);
        isFetchingRef.current = false;
        if (result.data?.myActions && result.data.myActions.length > 0) {
          console.log('[ActionsContext] Setting actions:', result.data.myActions.length, 'actions');
          setActions(new Set(result.data.myActions));
          setIsReady(true);
        } else if (result.error) {
          console.error('[ActionsContext] Query error:', result.error);
          setIsReady(true);
        } else {
          console.log('[ActionsContext] No actions returned');
          setIsReady(true);
        }
      }).catch(err => {
        console.error('[ActionsContext] Failed to fetch user actions:', err);
        isFetchingRef.current = false;
        setIsReady(true);
      });
    } else if (!isAuthNow && !hasTokenNow && !globalFetched) {
      // Only clear actions if we're SURE user is logged out AND we haven't successfully fetched
      if (actions.size > 0) {
        console.log('[ActionsContext] User logged out - clearing actions');
        setActionsState(new Set());
        clearGlobalActions();
      }
      setIsReady(true);
    } else if (isAuthNow && actions.size > 0) {
      // We have actions, mark as ready
      setIsReady(true);
    } else if (!isAuthNow && globalFetched) {
      // We think user logged out but we fetched successfully before - don't clear, wait for confirmation
      console.log('[ActionsContext] Ignoring false logout signal - globalFetched is true');
      setIsReady(true);
    }
  }, [effectivelyAuthenticated, hasToken, actions.size, isReady, fetchActions, setActions, checkCounter]);

  // Listen for actual logout events to clear window state
  useEffect(() => {
    const handleLogout = () => {
      console.log('[ActionsContext] Logout event - clearing window state');
      clearGlobalActions();
      setActionsState(new Set());
      setIsReady(true);
    };

    window.addEventListener('csc:logout', handleLogout);
    return () => window.removeEventListener('csc:logout', handleLogout);
  }, []);

  const hasAction = useCallback((action: string): boolean => {
    // Wildcard support: if user has "*" or "domain:*", they have all actions in that domain
    if (actions.has('*')) return true;
    if (actions.has(action)) return true;

    // Check for domain wildcard (e.g., "settings:*" grants "settings:write")
    const domain = action.split(':')[0];
    if (domain && actions.has(`${domain}:*`)) return true;

    return false;
  }, [actions]);

  const hasAnyAction = useCallback((actionList: readonly string[]): boolean => {
    return actionList.some(action => hasAction(action));
  }, [hasAction]);

  const hasAllActions = useCallback((actionList: readonly string[]): boolean => {
    return actionList.every(action => hasAction(action));
  }, [hasAction]);

  const refreshActions = useCallback(async (): Promise<void> => {
    if (effectivelyAuthenticated) {
      const result = await fetchActions();
      if (result.data?.myActions) {
        setActions(new Set(result.data.myActions));
        setIsReady(true);
      }
    }
  }, [effectivelyAuthenticated, fetchActions, setActions]);

  const value = useMemo<ActionsContextValue>(() => ({
    actions,
    isLoading: loading,
    isReady,
    hasAction,
    hasAnyAction,
    hasAllActions,
    refreshActions,
  }), [actions, loading, isReady, hasAction, hasAnyAction, hasAllActions, refreshActions]);

  return (
    <ActionsContext.Provider value={value}>
      {children}
    </ActionsContext.Provider>
  );
}

/**
 * Hook to access the actions context.
 * Must be used within an ActionsProvider.
 */
export function useActions(): ActionsContextValue {
  const context = useContext(ActionsContext);
  if (!context) {
    throw new Error('useActions must be used within an ActionsProvider');
  }
  return context;
}

/**
 * Hook to check if the current user has a specific action.
 * Returns false while loading, then the actual permission state.
 *
 * @param action - The action to check (e.g., "settings:write")
 * @returns boolean indicating if the user has the action
 */
export function useHasAction(action: string): boolean {
  const { hasAction, isReady } = useActions();
  return isReady && hasAction(action);
}

/**
 * Hook to check if the current user has any of the specified actions.
 *
 * @param actions - Array of actions to check
 * @returns boolean indicating if the user has any of the actions
 */
export function useHasAnyAction(actions: readonly string[]): boolean {
  const { hasAnyAction, isReady } = useActions();
  return isReady && hasAnyAction(actions);
}
